import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function AttendanceBadge({ pct }) {
  if (pct === null || pct === undefined) {
    return <span className="text-xs text-gray-400 italic">No data</span>;
  }
  const color = pct >= 80
    ? 'bg-emerald-100 text-emerald-700'
    : pct >= 60
    ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700';
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color}`}>
      {pct.toFixed(1)}%
    </span>
  );
}

function AttendanceBar({ pct, thin = false }) {
  if (pct === null || pct === undefined) return null;
  const clamped = Math.min(100, Math.max(0, pct));
  const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-400' : 'bg-red-400';
  const h = thin ? 'h-1' : 'h-1.5';
  return (
    <div className={`w-full bg-gray-100 rounded-full ${h} mt-1`}>
      <div className={`${h} rounded-full transition-all ${barColor}`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    ongoing:       'bg-emerald-100 text-emerald-700',
    completed:     'bg-blue-100 text-blue-700',
    'not started': 'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap ${cfg[status] || 'bg-gray-100 text-gray-500'}`}>
      {status || '—'}
    </span>
  );
}

function PersonAvatar({ name, imagePath, size = 'sm' }) {
  const [imgError, setImgError] = useState(false);
  const initials = (name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const dim = size === 'lg' ? 'w-9 h-9 text-[11px]' : size === 'xl' ? 'w-11 h-11 text-sm' : 'w-7 h-7 text-[9px]';
  const gradient = size === 'xl' ? 'from-indigo-100 to-teal-100' : size === 'lg' ? 'from-indigo-100 to-teal-100' : 'from-slate-100 to-blue-100';
  const textColor = size === 'xl' || size === 'lg' ? 'text-indigo-700' : 'text-blue-700';
  if (!imgError && imagePath) {
    return (
      <img
        src={getImageURL(imagePath)}
        alt={name}
        onError={() => setImgError(true)}
        className={`${dim} rounded-full shrink-0 object-cover border border-gray-200 shadow-sm`}
      />
    );
  }
  return (
    <div className={`${dim} rounded-full shrink-0 border border-gray-200 shadow-sm bg-gradient-to-br ${gradient} flex items-center justify-center`}>
      <span className={`font-bold ${textColor} leading-none`}>{initials}</span>
    </div>
  );
}

// ─── Student Detail Modal ─────────────────────────────────────────────────────

function StudentDetailModal({ subject, onClose }) {
  const [students, setStudents] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    axiosInstance.get('/admin/dashboard/batch-subject-attendance/students', {
      params: { batchSubjectId: subject.batchSubjectId },
    })
      .then((res) => {
        const list = (res.data?.students || []).slice()
          .sort((a, b) => (b.attendancePct ?? -1) - (a.attendancePct ?? -1));
        setStudents(list);
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [subject.batchSubjectId]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const totalClasses = students?.[0]?.totalClasses ?? subject.classesHeld ?? 0;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={onClose}
    >
      {/* Panel */}
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <StatusBadge status={subject.status} />
                {subject.subjectCode && (
                  <span className="text-[11px] text-gray-400 font-medium">{subject.subjectCode}</span>
                )}
              </div>
              <h2 className="text-base font-bold text-gray-900 leading-snug truncate">{subject.subjectName}</h2>
              {(subject.startDate || subject.endDate) && (
                <p className="text-[11px] text-gray-400 mt-0.5">{fmtDate(subject.startDate)} → {fmtDate(subject.endDate)}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Lecturer + stats row */}
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            {subject.staffId && (
              <div className="flex items-center gap-2">
                <PersonAvatar name={subject.staffName} imagePath={subject.staffImagePath} size="lg" />
                <div>
                  <p className="text-xs font-semibold text-gray-700 leading-tight">{subject.staffName}</p>
                  <p className="text-[10px] text-gray-400">Lecturer</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3 ml-auto flex-wrap">
              <div className="text-center">
                <p className="text-lg font-bold text-gray-900">{subject.enrolledCount}</p>
                <p className="text-[10px] text-gray-400">enrolled</p>
              </div>
              <div className="w-px h-8 bg-gray-200" />
              <div className="text-center">
                <p className="text-lg font-bold text-gray-900">{totalClasses}</p>
                <p className="text-[10px] text-gray-400">classes held</p>
              </div>
              <div className="w-px h-8 bg-gray-200" />
              <div className="text-center">
                <AttendanceBadge pct={subject.attendancePct} />
                <p className="text-[10px] text-gray-400 mt-0.5">avg attendance</p>
              </div>
            </div>
          </div>

          {/* Formula hint */}
          <p className="text-[10px] text-gray-400 mt-2 italic">
            Formula: classes present ÷ {totalClasses} × 100 per student
          </p>
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-12 px-6 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
          <div className="col-span-4">Student</div>
          <div className="col-span-2 text-center">Scheduled</div>
          <div className="col-span-2 text-center">Present</div>
          <div className="col-span-1 text-center">Absent</div>
          <div className="col-span-3 text-center">Attendance %</div>
        </div>

        {/* Scrollable student list */}
        <div className="overflow-y-auto flex-1">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-gray-400 text-sm">
              <svg className="animate-spin w-4 h-4 text-teal-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Loading student attendance…
            </div>
          )}

          {error && (
            <p className="px-6 py-4 text-sm text-red-500 text-center">{error}</p>
          )}

          {!loading && !error && students?.length === 0 && (
            <p className="px-6 py-10 text-sm text-gray-400 text-center italic">No enrolled students found.</p>
          )}

          {!loading && !error && students?.map((st, idx) => (
            <div
              key={st.studentId}
              className="grid grid-cols-12 items-center px-6 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50/70 transition-colors"
            >
              {/* Rank + Student */}
              <div className="col-span-4 flex items-center gap-3 min-w-0">
                <span className="text-[11px] text-gray-300 font-semibold w-4 shrink-0 text-right">{idx + 1}</span>
                <PersonAvatar name={st.studentName} imagePath={st.imagePath} size="lg" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 leading-snug break-words">{st.studentName}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    <span className="font-medium text-gray-500">{st.studentId}</span>
                    {st.registrationNumber && (
                      <span className="ml-1.5">· {st.registrationNumber}</span>
                    )}
                  </p>
                  {st.telephone && (
                    <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-2.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 0 1 2-2h3.28a1 1 0 0 1 .948.684l1.498 4.493a1 1 0 0 1-.502 1.21l-2.257 1.13a11.042 11.042 0 0 0 5.516 5.516l1.13-2.257a1 1 0 0 1 1.21-.502l4.493 1.498A1 1 0 0 1 21 15.72V19a2 2 0 0 1-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {st.telephone}
                    </p>
                  )}
                </div>
              </div>

              {/* Scheduled */}
              <div className="col-span-2 text-center">
                <span className="text-sm font-semibold text-gray-700">{totalClasses}</span>
              </div>

              {/* Present */}
              <div className="col-span-2 text-center">
                <span className="text-sm font-bold text-emerald-600">{st.classesPresent}</span>
              </div>

              {/* Absent */}
              <div className="col-span-1 text-center">
                <span className="text-sm font-bold text-red-400">{st.classesAbsent}</span>
              </div>

              {/* % */}
              <div className="col-span-3 px-2">
                <div className="flex justify-center mb-0.5">
                  <AttendanceBadge pct={st.attendancePct} />
                </div>
                <AttendanceBar pct={st.attendancePct} thin />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function BatchSubjectAttendanceReport() {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');
  const [expandedBatches, setExpandedBatches] = useState(new Set());
  const [statusFilter, setStatusFilter]       = useState('all');
  const [selectedCourse, setSelectedCourse]   = useState(null);
  const [modalSubject, setModalSubject]       = useState(null);

  useEffect(() => {
    axiosInstance.get('/admin/dashboard/batch-subject-attendance')
      .then((res) => {
        setData(res.data);
        if (res.data?.batches) {
          setExpandedBatches(new Set(res.data.batches.map((b) => b.batchId)));
        }
      })
      .catch((err) => setError(err?.response?.data?.message || err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const courses = useMemo(() => {
    if (!data?.batches) return [];
    const seen = new Map();
    data.batches.forEach((b) => {
      const key = b.courseName || '';
      if (!key) return;
      if (!seen.has(key)) {
        seen.set(key, { courseName: key, courseShortName: b.courseShortName || key, batchCount: 0 });
      }
      seen.get(key).batchCount += 1;
    });
    return Array.from(seen.values()).sort((a, b) => a.courseName.localeCompare(b.courseName));
  }, [data]);

  const filteredBatches = useMemo(() => {
    if (!data?.batches) return [];
    const q = search.toLowerCase();
    return data.batches
      .filter((batch) => {
        if (selectedCourse && batch.courseName !== selectedCourse) return false;
        if (!q) return true;
        return (
          batch.batchName?.toLowerCase().includes(q) ||
          batch.batchInternalName?.toLowerCase().includes(q) ||
          batch.whatsappGroupName?.toLowerCase().includes(q) ||
          batch.courseName?.toLowerCase().includes(q) ||
          batch.subjects.some(
            (s) =>
              s.subjectName?.toLowerCase().includes(q) ||
              s.subjectCode?.toLowerCase().includes(q) ||
              s.staffName?.toLowerCase().includes(q)
          )
        );
      })
      .map((batch) => {
        const filtered = statusFilter === 'all'
          ? batch.subjects
          : batch.subjects.filter((s) => s.status === statusFilter);
        return {
          ...batch,
          subjects: filtered.slice().sort((a, b) => (b.attendancePct ?? -1) - (a.attendancePct ?? -1)),
        };
      })
      .filter((batch) => batch.subjects.length > 0);
  }, [data, search, statusFilter, selectedCourse]);

  const toggleBatch = useCallback((batchId) => {
    setExpandedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });
  }, []);

  const openModal = useCallback((subj) => setModalSubject(subj), []);
  const closeModal = useCallback(() => setModalSubject(null), []);

  const downloadExcel = useCallback(() => {
    if (!filteredBatches.length) return;

    const shortNameMap = new Map(courses.map((c) => [c.courseName, c.courseShortName]));

    // Group filtered batches by course
    const grouped = new Map();
    filteredBatches.forEach((batch) => {
      const key = batch.courseName || 'Unknown';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(batch);
    });

    const wb = XLSX.utils.book_new();
    const HEADERS = ['Batch', 'Subject', 'Code', 'Lecturer', 'Status', 'Enrolled', 'Scheduled Classes', 'Total Present', 'Attendance %'];

    grouped.forEach((batches, courseName) => {
      const aoa = [
        [`Course: ${courseName}`],
        [],
        HEADERS,
      ];

      batches.forEach((batch) => {
        const batchLabel = batch.whatsappGroupName || batch.batchInternalName || batch.batchName || `Batch #${batch.batchId}`;
        batch.subjects.forEach((subj) => {
          aoa.push([
            batchLabel,
            subj.subjectName || '',
            subj.subjectCode || '',
            subj.staffName || 'Unassigned',
            subj.status || '',
            subj.enrolledCount ?? 0,
            subj.classesHeld ?? 0,
            subj.totalPresent ?? 0,
            subj.attendancePct !== null && subj.attendancePct !== undefined
              ? `${subj.attendancePct.toFixed(1)}%`
              : 'No data',
          ]);
        });
        aoa.push([]);
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [
        { wch: 22 }, { wch: 42 }, { wch: 14 }, { wch: 24 },
        { wch: 12 }, { wch: 10 }, { wch: 16 }, { wch: 14 }, { wch: 14 },
      ];

      const shortName = shortNameMap.get(courseName) || courseName;
      const sheetName = shortName.replace(/[/\\?*[\]:]/g, '-').slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    const suffix = selectedCourse
      ? `_${(shortNameMap.get(selectedCourse) || selectedCourse).replace(/[^a-zA-Z0-9]/g, '_')}`
      : '_all_courses';

    XLSX.writeFile(wb, `batch_subject_attendance${suffix}_${today}.xlsx`);
  }, [filteredBatches, courses, selectedCourse]);

  const totalBatches   = data?.batches?.length || 0;
  const totalOngoing   = data?.batches?.reduce((n, b) => n + b.ongoingCount, 0) || 0;
  const totalCompleted = data?.batches?.reduce((n, b) => n + b.completedCount, 0) || 0;

  return (
    <DashboardLayout title="Batch-wise Subject Attendance">
      <div className="min-h-screen bg-gray-50 px-6 py-8">

        {/* Header */}
        <div className="mb-6 border-b border-gray-200 pb-5">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">Batch-wise Subject Attendance</h1>
          </div>
          <p className="text-sm text-gray-500 ml-11">
            Subject-level attendance for ongoing batches. Click the student count button to view individual student breakdown.
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-24 text-gray-400">
            <svg className="animate-spin w-5 h-5 mr-2 text-teal-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm">Loading attendance data…</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 text-sm text-red-700">{error}</div>
        )}

        {!loading && !error && data && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
                <p className="text-xs text-gray-500 mb-1">Total Batches</p>
                <p className="text-2xl font-bold text-gray-900">{totalBatches}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
                <p className="text-xs text-gray-500 mb-1">Ongoing Subjects</p>
                <p className="text-2xl font-bold text-emerald-700">{totalOngoing}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
                <p className="text-xs text-gray-500 mb-1">Completed Subjects</p>
                <p className="text-2xl font-bold text-blue-700">{totalCompleted}</p>
              </div>
            </div>

            {/* Course tabs */}
            {courses.length > 1 && (
              <div className="mb-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCourse(null)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    selectedCourse === null
                      ? 'bg-teal-600 border-teal-600 text-white shadow-sm'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-700'
                  }`}
                >
                  All courses
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                    selectedCourse === null ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {data?.batches?.length || 0}
                  </span>
                </button>
                {courses.map((c) => (
                  <button
                    key={c.courseName}
                    type="button"
                    title={c.courseName}
                    onClick={() => setSelectedCourse(c.courseName)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                      selectedCourse === c.courseName
                        ? 'bg-teal-600 border-teal-600 text-white shadow-sm'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-700'
                    }`}
                  >
                    {c.courseShortName}
                    <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                      selectedCourse === c.courseName ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {c.batchCount}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Filters */}
            <div className="flex gap-3 mb-5">
              <div className="relative flex-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search batch, subject, or lecturer…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white shadow-sm">
                {[
                  { key: 'all',       label: 'All' },
                  { key: 'ongoing',   label: 'Ongoing' },
                  { key: 'completed', label: 'Completed' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setStatusFilter(key)}
                    className={`px-3 py-2 text-xs font-medium transition-colors ${statusFilter === key ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Download Excel */}
              <button
                type="button"
                onClick={downloadExcel}
                disabled={!filteredBatches.length}
                title={selectedCourse ? `Download ${selectedCourse} as Excel` : 'Download all courses as Excel (one sheet per course)'}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {selectedCourse ? 'Export' : 'Export All'}
              </button>
            </div>

            {/* Batch cards */}
            <div className="space-y-4">
              {filteredBatches.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
                  </svg>
                  <p className="text-sm">No batches match your filter.</p>
                </div>
              )}

              {filteredBatches.map((batch) => {
                const isBatchExpanded = expandedBatches.has(batch.batchId);
                const displayName = batch.whatsappGroupName || batch.batchInternalName || batch.batchName || `Batch #${batch.batchId}`;
                return (
                  <div key={batch.batchId} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

                    {/* Batch header */}
                    <button
                      type="button"
                      onClick={() => toggleBatch(batch.batchId)}
                      className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/60 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2m14 0V9a2 2 0 0 0-2-2M5 11V9a2 2 0 0 1 2-2m0 0V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2M7 7h10" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
                          <p className="text-xs text-gray-400 truncate">{batch.courseName} · {batch.enrolledCount} students</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5 shrink-0 ml-4">
                        <span className="text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">{batch.ongoingCount} ongoing</span>
                        <span className="text-[11px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">{batch.completedCount} completed</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isBatchExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>

                    {/* Subject list */}
                    {isBatchExpanded && (
                      <div className="border-t border-gray-100">

                        {/* Table header */}
                        <div className="grid grid-cols-12 px-5 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50/80 border-b border-gray-100">
                          <div className="col-span-3">Subject</div>
                          <div className="col-span-3">Lecturer</div>
                          <div className="col-span-2 text-center">Status</div>
                          <div className="col-span-1 text-center">Students</div>
                          <div className="col-span-1 text-center">Scheduled</div>
                          <div className="col-span-2 text-center">Attendance %</div>
                        </div>

                        {batch.subjects.map((subj) => (
                          <div
                            key={subj.batchSubjectId}
                            className="grid grid-cols-12 px-5 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50/50 items-center gap-x-2"
                          >
                            {/* Subject */}
                            <div className="col-span-3 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{subj.subjectName}</p>
                              {subj.subjectCode && (
                                <p className="text-[11px] text-gray-400">{subj.subjectCode}</p>
                              )}
                              {subj.startDate && (
                                <p className="text-[10px] text-gray-400 mt-0.5">{fmtDate(subj.startDate)} → {fmtDate(subj.endDate)}</p>
                              )}
                            </div>

                            {/* Lecturer */}
                            <div className="col-span-3 min-w-0">
                              {subj.staffId ? (
                                <div className="flex items-center gap-2 min-w-0">
                                  <PersonAvatar name={subj.staffName} imagePath={subj.staffImagePath} size="lg" />
                                  <p className="text-xs font-medium text-gray-700 truncate leading-tight">{subj.staffName}</p>
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400 italic">Unassigned</p>
                              )}
                            </div>

                            {/* Status */}
                            <div className="col-span-2 flex justify-center">
                              <StatusBadge status={subj.status} />
                            </div>

                            {/* Students button — opens modal */}
                            <div className="col-span-1 flex justify-center">
                              <button
                                type="button"
                                onClick={() => openModal(subj)}
                                title="View student-wise attendance"
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-teal-50 border border-teal-200 text-teal-700 hover:bg-teal-100 transition-colors"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                                </svg>
                                {subj.enrolledCount}
                              </button>
                            </div>

                            {/* Classes */}
                            <div className="col-span-1 text-center">
                              <p className="text-sm text-gray-700 font-medium">{subj.classesHeld}</p>
                              {subj.classesHeld > 0 && (
                                <p className="text-[10px] text-gray-400">{subj.totalPresent}P</p>
                              )}
                            </div>

                            {/* Attendance % */}
                            <div className="col-span-2 px-1">
                              <div className="flex justify-center">
                                <AttendanceBadge pct={subj.attendancePct} />
                              </div>
                              <AttendanceBar pct={subj.attendancePct} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Student detail modal */}
      {modalSubject && (
        <StudentDetailModal subject={modalSubject} onClose={closeModal} />
      )}
    </DashboardLayout>
  );
}
